import React, { useState, useEffect, useRef } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import styles from "./CheatSheetPage.module.scss";
import Header from "../../header/header";
import Papa from "papaparse";
import { db } from "../../../firebase";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { CustomSpinner } from "../../shared/Spinner";
import { CSVLink } from "react-csv";
import { FiDownload, FiX, FiPlus } from "react-icons/fi";
import {
  MdEdit,
  MdDelete,
  MdVisibility,
  MdSave,
} from "react-icons/md";
import { toast } from "react-toastify";
import { formatWeightDisplay } from "../../../utils/helpers";

const DraggableRow = ({
  player,
  background,
  index,
  moveRow,
  removePlayer,
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: "ROW",
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [, drop] = useDrop({
    accept: "ROW",
    hover: (draggedItem, monitor) => {
      if (draggedItem.index === index) return;
      const hoverRect = document
        .querySelector(`[data-row-id="${player.id}"]`)
        ?.getBoundingClientRect();
      if (!hoverRect) return;
      const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverRect.top;
      if (
        (draggedItem.index < index && hoverClientY < hoverMiddleY) ||
        (draggedItem.index > index && hoverClientY > hoverMiddleY)
      )
        return;
      moveRow(draggedItem.index, index);
      draggedItem.index = index;
    },
  });

  return (
    <tr
      ref={(node) => drag(drop(node))}
      className={`${styles.draggableRow} ${isDragging ? styles.dragging : ""}`}
      data-row-id={player.id}
    >
      <td>
        {player.FirstName} {player.LastName}
      </td>
      <td>{player.Position || "--"}</td>
      <td>{player.Year || "--"}</td>
      <td>{player.Age || "--"}</td>
      <td>{player.Team || "--"}</td>
      <td>{background.cheatSheet?.playerContact?.phone || "--"}</td>
      <td>{background.cheatSheet?.nuggets || "--"}</td>
      <td>
        <FiX
          className={styles.removeIcon}
          onClick={() => {
            if (
              window.confirm(
                "Are you sure you want to remove this player from the sheet?"
              )
            ) {
              removePlayer(player.id);
            }
          }}
          title="Remove player"
        />
      </td>
    </tr>
  );
};

const SheetManager = ({
  cheatSheets,
  onCreateNew,
  onSelectSheet,
  onDeleteSheet,
  onEditSheet,
  newSheetName,
  setNewSheetName,
}) => {
  const sortedSheets = [...cheatSheets].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  return (
    <div className={styles.sheetManager}>
      <h4>Cheat Sheets</h4>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Title</th>
            <th>Created Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedSheets.length === 0 ? (
            <tr>
              <td colSpan="3">No cheat sheets yet.</td>
            </tr>
          ) : (
            sortedSheets.map((sheet) => (
              <tr key={sheet.id}>
                <td>{sheet.name}</td>
                <td>{new Date(sheet.createdAt).toLocaleDateString()}</td>
                <td className={styles.actionsCell}>
                  <MdVisibility
                    className={styles.actionIcon}
                    onClick={() => onSelectSheet(sheet.id)}
                    title="View"
                  />
                  <MdEdit
                    className={styles.actionIcon}
                    onClick={() => onEditSheet(sheet)}
                    title="Edit"
                  />
                  <MdDelete
                    className={styles.actionIcon}
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to delete this cheat sheet?"
                        )
                      ) {
                        onDeleteSheet(sheet.id);
                      }
                    }}
                    title="Delete"
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className={styles.newSheetForm}>
        <input
          type="text"
          placeholder="New sheet name"
          value={newSheetName}
          onChange={(e) => setNewSheetName(e.target.value)}
        />
        <button
          onClick={onCreateNew}
          style={{
            backgroundColor: "#000",
            color: "#fff",
            border: "none",
            padding: "8px 16px",
            cursor: "pointer",
          }}
        >
          Create New Sheet
        </button>
      </div>
    </div>
  );
};

export default function CheatSheetGenerator() {
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [playersData, setPlayersData] = useState([]);
  const [backgroundData, setBackgroundData] = useState({});
  const [cheatSheets, setCheatSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [showSheetManager, setShowSheetManager] = useState(false);
  const [newSheetName, setNewSheetName] = useState("");
  const [editingSheet, setEditingSheet] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  const currentSheet = cheatSheets.find((sheet) => sheet.id === selectedSheet);

  useEffect(() => {
    let unsubscribe = null;

    const fetchData = async () => {
      try {
        const playersSnapshot = await getDocs(
          collection(db, "player-profile")
        );
        const players = playersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const backgroundSnapshot = await getDocs(
          collection(db, "background-overview")
        );
        const backgrounds = {};
        backgroundSnapshot.forEach((doc) => {
          const bgData = doc.data();
          const playerId = bgData.playerProfile?.id || bgData.playerProfile;
          if (playerId) backgrounds[playerId] = bgData;
        });

        const cheatSheetsQuery = query(
          collection(db, "cheat-sheets"),
          orderBy("createdAt", "desc")
        );

        unsubscribe = onSnapshot(cheatSheetsQuery, 
          (snapshot) => {
            const sheets = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate().toISOString(),
            }));
            setCheatSheets(sheets);
          },
          (error) => {
            console.error("Firestore listener error:", error);
            toast.error("Error loading cheat sheets");
          }
        );

        setPlayersData(players);
        setBackgroundData(backgrounds);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Error loading data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []); // Remove unsubscribeSheets from dependencies

  useEffect(() => {
    setIsEditingTitle(false);
    setEditingSheet(null);
  }, [selectedSheet]);

  const searchContainerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const container = searchContainerRef.current;
      if (container && !container.contains(e.target)) {
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const createNewCheatSheet = async () => {
    if (!newSheetName.trim()) return;
    try {
      const newSheet = {
        name: newSheetName.trim(),
        players: [],
        createdAt: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, "cheat-sheets"), newSheet);
      setSelectedSheet(docRef.id);
      setNewSheetName("");
      setShowSheetManager(false);
    } catch (error) {
      toast.error("Failed to create new sheet");
    }
  };

  const updateCheatSheet = async (sheetId, updates) => {
    try {
      const sheetRef = doc(db, "cheat-sheets", sheetId);
      await updateDoc(sheetRef, updates);
    } catch (error) {
      toast.error("Failed to update sheet");
      console.error("Update error:", error);
    }
  };

  const deleteCheatSheet = async (sheetId) => {
    try {
      setCheatSheets((prev) => prev.filter((s) => s.id !== sheetId));
      if (selectedSheet === sheetId) setSelectedSheet(null);
      await deleteDoc(doc(db, "cheat-sheets", sheetId));
    } catch (error) {
      toast.error("Failed to delete sheet");
      const q = query(
        collection(db, "cheat-sheets"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(q);
      setCheatSheets(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    }
  };

  const addPlayer = async (playerId) => {
    if (!currentSheet || currentSheet.players.includes(playerId)) return;

    const newPlayers = [...currentSheet.players, playerId];
    try {
      setCheatSheets((prev) =>
        prev.map((sheet) =>
          sheet.id === currentSheet.id ? { ...sheet, players: newPlayers } : sheet
        )
      );
      await updateCheatSheet(currentSheet.id, { players: newPlayers });
    } catch (error) {
      setCheatSheets((prev) =>
        prev.map((sheet) =>
          sheet.id === currentSheet.id ? currentSheet : sheet
        )
      );
    }
    setSearchQuery("");
  };

  const removePlayer = async (playerId) => {
    if (!currentSheet) return;

    const newPlayers = currentSheet.players.filter((id) => id !== playerId);
    try {
      setCheatSheets((prev) =>
        prev.map((sheet) =>
          sheet.id === currentSheet.id ? { ...sheet, players: newPlayers } : sheet
        )
      );
      await updateCheatSheet(currentSheet.id, { players: newPlayers });
    } catch (error) {
      setCheatSheets((prev) =>
        prev.map((sheet) =>
          sheet.id === currentSheet.id ? currentSheet : sheet
        )
      );
    }
  };

  const moveRow = async (fromIndex, toIndex) => {
    if (!currentSheet) return;

    const arr = [...currentSheet.players];
    const [moved] = arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, moved);

    try {
      setCheatSheets((prev) =>
        prev.map((sheet) =>
          sheet.id === currentSheet.id ? { ...sheet, players: arr } : sheet
        )
      );
      await updateCheatSheet(currentSheet.id, { players: arr });
    } catch (error) {
      setCheatSheets((prev) =>
        prev.map((sheet) =>
          sheet.id === currentSheet.id ? currentSheet : sheet
        )
      );
    }
  };

  const formatDOB = (dobString) => {
    if (!dobString) return "";
    const parts = dobString.split("-");
    return parts.length === 3
      ? `${parts[1]}.${parts[2]}.${parts[0]}`
      : dobString;
  };

  const getCSVData = () => {
    if (!currentSheet) return [];
    return currentSheet.players.map((pid) => {
      const p = playersData.find((x) => x.id === pid) || {};
      const bg = backgroundData[pid] || {};
      const teamHist = bg.teamHistory || [];
      // Prefer the most recently-entered team history item (array order).
      // Sorting by year breaks when multiple entries exist for the same year.
      const latest = teamHist.length > 0 ? teamHist[teamHist.length - 1] : {};
      const status = p.Status || "";
      const comm = p.committedTeam || "";
      const recruitment =
        status === "Undecided"
          ? status
          : comm
          ? `${status}: ${comm}`
          : status;
      return {
        "Industry Ranking Average": p.rankAverage,
        "Player Name": `${p.FirstName} ${p.LastName}`,
        Recruitment: recruitment,
        "Player Contact": bg.cheatSheet?.playerContact?.phone,
        Year: p.Year,
        "HT.": p.Height,
        "WT.": formatWeightDisplay(p.Weight, ""),
        "WS.": p.Wingspan,
        Hand: p.Hand,
        "Pos.": p.Position,
        Birthday: formatDOB(p.DOB),
        Hometown: `${p.hometown_City}, ${p.hometown_State_Country}`,
        Team:
          latest.school && latest.state
            ? `${latest.school} (${latest.state})`
            : "",
        "HS Coach":
          latest.coachFirstName
            ? `${latest.coachFirstName} ${latest.coachLastName}`
            : "",
        "HS Coach Contact": latest.phone || "",
        "AAU Team": p.aauTeam,
        "AAU Coach Name": `${p.aauCoach} ${p.aauCoachLast}`,
        "AAU Contact": p.aauCoachInfo,
        Instagram: p.Instagram,
        Nuggets: bg.cheatSheet?.nuggets,
      };
    });
  };

  const getContactCSVData = () => {
    if (!currentSheet) return [];
    return currentSheet.players.map((pid) => {
      const p = playersData.find((x) => x.id === pid) || {};
      const bg = backgroundData[pid] || {};
      return {
        "Player Name": `${p.FirstName} ${p.LastName}`,
        "Personal Number": bg.cheatSheet?.playerContact?.phone || "",
      };
    });
  };

  const downloadCSV = (rows, filename) => {
    if (!rows?.length) return;

    const normalizedRows = rows.map((row) =>
      Object.fromEntries(
        Object.entries(row).map(([key, value]) => [key, value ?? ""])
      )
    );
    const csv = Papa.unparse(normalizedRows, { quotes: true });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const filteredPlayers = playersData.filter((player) =>
    `${player.FirstName} ${player.LastName} ${player.Team} ${player.Position}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  if (loading) return <CustomSpinner />;

  return (
    <div className={styles.container}>
      <Header />
      <div className={styles.content}>
        {(showSheetManager || !currentSheet) ? (
          <SheetManager
            cheatSheets={cheatSheets}
            onCreateNew={createNewCheatSheet}
            onSelectSheet={(id) => {
              setSelectedSheet(id);
              setShowSheetManager(false);
            }}
            onDeleteSheet={deleteCheatSheet}
            onEditSheet={(sheet) => {
              setEditingSheet(sheet);
              setSelectedSheet(sheet.id);
              setShowSheetManager(false);
            }}
            newSheetName={newSheetName}
            setNewSheetName={setNewSheetName}
          />
        ) : (
          <>
            <div className={styles.headerRow}>
              <div className={styles.sheetHeader}>
                <button
                  onClick={() => setShowSheetManager(true)}
                  className={styles.backButton}
                >
                  ← Back to Sheets
                </button>
                <div className={styles.titleContainer}>
                  <input
                    type="text"
                    value={editingSheet?.name || currentSheet.name}
                    onChange={(e) =>
                      setEditingSheet({ ...currentSheet, name: e.target.value })
                    }
                    className={styles.sheetNameInput}
                    disabled={!isEditingTitle}
                  />
                  {isEditingTitle ? (
                    <MdSave
                      className={styles.saveIcon}
                      onClick={() => {
                        updateCheatSheet(currentSheet.id, {
                          name: editingSheet.name,
                        });
                        setIsEditingTitle(false);
                        setEditingSheet(null);
                      }}
                    />
                  ) : (
                    <MdEdit
                      className={styles.editIcon}
                      onClick={() => setIsEditingTitle(true)}
                    />
                  )}
                </div>
              </div>
              <div className={styles.controls}>
                <div className={styles.searchContainer} ref={searchContainerRef}>
                  <input
                    type="text"
                    placeholder="Search players..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <div className={styles.searchResults}>
                      {filteredPlayers.map((player) => (
                        <div
                          key={player.id}
                          className={styles.searchResult}
                          onClick={() => addPlayer(player.id)}
                        >
                          {player.FirstName} {player.LastName} ({player.Year})
                          <FiPlus />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {currentSheet.players.length > 0 && (
                  <>
                    <CSVLink
                      data={[]}
                      filename={`${currentSheet.name}-cheat-sheet.csv`}
                      className={styles.exportButton}
                      onClick={(e) => {
                        e.preventDefault();
                        downloadCSV(
                          getCSVData(),
                          `${currentSheet.name}-cheat-sheet.csv`
                        );
                      }}
                    >
                      <FiDownload /> Export CSV
                    </CSVLink>
                    <CSVLink
                      data={[]}
                      filename={`${currentSheet.name}-contacts.csv`}
                      className={styles.exportButton}
                      onClick={(e) => {
                        e.preventDefault();
                        downloadCSV(
                          getContactCSVData(),
                          `${currentSheet.name}-contacts.csv`
                        );
                      }}
                    >
                      <FiDownload /> Export Contacts
                    </CSVLink>
                  </>
                )}
              </div>
            </div>
            <DndProvider backend={HTML5Backend}>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Position</th>
                      <th>Year</th>
                      <th>Age</th>
                      <th>Team</th>
                      <th>Personal Number</th>
                      <th>Nuggets</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentSheet.players.length === 0 ? (
                      <tr className={styles.emptyRow}>
                        <td colSpan="8">
                          No players selected – search above to add players
                        </td>
                      </tr>
                    ) : (
                      currentSheet.players.map((playerId, index) => {
                        const player =
                          playersData.find((p) => p.id === playerId) || {};
                        const background = backgroundData[playerId] || {};
                        return (
                          <DraggableRow
                            key={playerId}
                            index={index}
                            player={player}
                            background={background}
                            moveRow={moveRow}
                            removePlayer={removePlayer}
                          />
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </DndProvider>
          </>
        )}
      </div>
    </div>
  );
}
